import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/api";
import { useLang, SECTORS } from "@/i18n";
import Reveal from "@/components/Reveal";
import RelatedProperties from "@/components/RelatedProperties";
import DateInput from "@/components/DateInput";

export default function ContactPage() {
  const { t, simg } = useLang();
  const [searchParams] = useSearchParams();
  const sujet = searchParams.get("sujet");
  const [properties, setProperties] = useState([]);
  const [form, setForm] = useState({
    first_name: "", last_name: "", phone: "", email: "",
    location: sujet === "location", gestion: sujet === "gestion",
    guests: "", property_id: "", arrival: "", departure: "",
    property_type: "", sector: "", message: "",
  });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/properties").then((r) => setProperties(r.data)).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggleCase = (k) => setForm((f) => ({ ...f, [k]: !f[k] }));
  const maxGuests = properties.find((p) => p.id === form.property_id)?.guests || 15;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.location && !form.gestion) {
      setError(t.contact.select_one);
      return;
    }
    setError("");
    setSending(true);
    const kind = form.location && form.gestion ? "mixte" : form.location ? "location" : "gestion";
    try {
      await api.post("/contact", {
        first_name: form.first_name,
        last_name: form.last_name,
        phone: form.phone,
        email: form.email,
        kind,
        message: form.message,
        guests: form.location && form.guests ? Number(form.guests) : null,
        property_id: form.location && form.property_id ? form.property_id : null,
        arrival: form.location && form.arrival ? form.arrival : null,
        departure: form.location && form.departure ? form.departure : null,
        property_type: form.gestion && form.property_type ? form.property_type : null,
        sector: form.gestion && form.sector ? form.sector : null,
      });
      setDone(true);
      toast.success(t.contact.success);
      setForm({
        first_name: "", last_name: "", phone: "", email: "",
        location: false, gestion: false,
        guests: "", property_id: "", arrival: "", departure: "",
        property_type: "", sector: "", message: "",
      });
    } catch (err) {
      toast.error(formatApiError(err) || t.contact.error);
    } finally {
      setSending(false);
    }
  };

  return (
    <main data-testid="contact-page">
      <header className="relative flex h-[50vh] min-h-[400px] items-end overflow-hidden">
        <img src={simg("mountainSea")} alt="Montagnes corses" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-mer/80 via-mer/30 to-mer/40" />
        <div className="relative mx-auto w-full max-w-7xl px-5 sm:px-8 pb-14 text-casse">
          <p className="eyebrow text-sable">{t.contact.eyebrow}</p>
          <h1 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl">{t.contact.title}</h1>
          <p className="mt-4 text-justify text-sm sm:text-base text-casse/85">{t.contact.sub}</p>
        </div>
      </header>

      <div className="mx-auto grid max-w-7xl gap-16 px-5 sm:px-8 py-20 sm:py-28 lg:grid-cols-2">
        <Reveal className="flex flex-col lg:order-1">
          <div className="mx-auto w-full max-w-md flex-1 text-center lg:pt-24">
            <p data-testid="contact-intro-owners" className="text-base leading-relaxed text-maquis-olive">{t.contact.intro_owners}</p>
            <p data-testid="contact-intro-travelers" className="mt-6 text-base leading-relaxed text-maquis-olive">{t.contact.intro_travelers}</p>
          </div>
          <dl className="mx-auto mt-24 w-full max-w-md space-y-6 text-center text-sm" data-testid="contact-coordinates">
            <div>
              <dt className="field-label font-semibold">{t.contact.address.split(",")[0]}</dt>
              <dd data-testid="contact-address" className="text-maquis-deep">{t.contact.address}</dd>
            </div>
            <div>
              <dt className="field-label font-semibold">{t.contact.phone_label}</dt>
              <dd data-testid="contact-phone" className="text-maquis-deep">+33 6 03 27 46 10</dd>
            </div>
            <div>
              <dt className="field-label font-semibold">{t.contact.email_label}</dt>
              <dd>
                <a href="mailto:caropv2a@gmail.com" data-testid="contact-email" className="text-maquis-deep underline decoration-sable underline-offset-4 hover:decoration-maquis-olive transition-colors">
                  caropv2a@gmail.com
                </a>
              </dd>
            </div>
          </dl>
        </Reveal>

        <Reveal delay={0.1} className="lg:order-2">
          <div className="mx-auto w-full max-w-xl">
          {done && (
            <p data-testid="contact-success-message" className="mt-6 rounded-md border border-maquis-olive/30 bg-ivoire p-4 text-sm text-maquis-olive">
              {t.contact.success}
            </p>
          )}
          <form onSubmit={submit} className="mt-14 space-y-8" data-testid="contact-form">
            <fieldset>
              <legend className="eyebrow mb-4">{t.contact.part1}</legend>
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label className="field-label">{t.contact.name} *</label>
                  <input data-testid="contact-lastname-input" type="text" required value={form.last_name} onChange={set("last_name")} className="field-input" />
                </div>
                <div>
                  <label className="field-label">{t.contact.firstname} *</label>
                  <input data-testid="contact-firstname-input" type="text" required value={form.first_name} onChange={set("first_name")} className="field-input" />
                </div>
                <div>
                  <label className="field-label">{t.contact.phone} *</label>
                  <input data-testid="contact-phone-input" type="tel" required value={form.phone} onChange={set("phone")} className="field-input" />
                </div>
                <div>
                  <label className="field-label">{t.contact.email} *</label>
                  <input data-testid="contact-email-input" type="email" required value={form.email} onChange={set("email")} className="field-input" />
                </div>
              </div>
              <div className="mt-6">
                <p className="field-label">{t.contact.request_type} *</p>
                <div className="flex flex-wrap gap-3">
                  <button
                    type="button"
                    data-testid="contact-case-location"
                    onClick={() => toggleCase("location")}
                    className={`rounded-full border px-6 py-2.5 text-xs uppercase tracking-[0.15em] transition-colors duration-200 ${
                      form.location
                        ? "border-maquis-olive bg-maquis-olive text-casse"
                        : "border-maquis-deep/25 text-maquis-deep/80 hover:border-maquis-olive"
                    }`}
                  >
                    {t.contact.case_location}
                  </button>
                  <button
                    type="button"
                    data-testid="contact-case-gestion"
                    onClick={() => toggleCase("gestion")}
                    className={`rounded-full border px-6 py-2.5 text-xs uppercase tracking-[0.15em] transition-colors duration-200 ${
                      form.gestion
                        ? "border-maquis-olive bg-maquis-olive text-casse"
                        : "border-maquis-deep/25 text-maquis-deep/80 hover:border-maquis-olive"
                    }`}
                  >
                    {t.contact.case_gestion}
                  </button>
                </div>
                {error && <p data-testid="contact-kind-error" className="mt-3 text-sm text-red-800">{error}</p>}
              </div>
            </fieldset>

            {form.location && (
              <fieldset data-testid="contact-location-fields">
                <legend className="eyebrow mb-4">{t.contact.loc_section}</legend>
                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <label className="field-label">{t.contact.loc_guests}</label>
                    <select
                      data-testid="contact-loc-guests"
                      disabled={!properties.length}
                      value={form.guests === "" ? "" : Math.min(Number(form.guests) || 1, maxGuests)}
                      onChange={set("guests")}
                      className="field-input"
                    >
                      <option value="" disabled>{t.contact.guests_placeholder}</option>
                      {Array.from({ length: maxGuests }, (_, i) => i + 1).map((n) => (
                        <option key={n} value={n}>{n}</option>
                      ))}
                    </select>
                    {form.property_id && properties.length > 0 && (
                      <p data-testid="contact-guests-capacity-hint" className="mt-1 text-xs text-maquis-soft">
                        {t.contact.capacity_hint.replace("{n}", maxGuests)}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="field-label">{t.contact.loc_ref}</label>
                    <select data-testid="contact-loc-ref" value={form.property_id} onChange={set("property_id")} className="field-input">
                      <option value="">{t.contact.loc_ref_any}</option>
                      {properties.map((p) => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="field-label">{t.booking.arrival}</label>
                    <DateInput data-testid="contact-loc-arrival" value={form.arrival} onChange={set("arrival")} />
                  </div>
                  <div>
                    <label className="field-label">{t.booking.departure}</label>
                    <DateInput data-testid="contact-loc-departure" value={form.departure} onChange={set("departure")} />
                  </div>
                </div>
              </fieldset>
            )}

            {form.gestion && (
              <fieldset data-testid="contact-gestion-fields">
                <legend className="eyebrow mb-4">{t.contact.gest_section}</legend>
                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <label className="field-label">{t.contact.gest_type}</label>
                    <select data-testid="contact-gest-type" value={form.property_type} onChange={set("property_type")} className="field-input">
                      <option value="">—</option>
                      {Object.entries(t.types).map(([k, v]) => (
                        <option key={k} value={k}>{v}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="field-label">{t.contact.gest_sector}</label>
                    <select data-testid="contact-gest-sector" value={form.sector} onChange={set("sector")} className="field-input">
                      <option value="">—</option>
                      {SECTORS.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </fieldset>
            )}

            {(form.location || form.gestion) && (
              <div>
                <label className="field-label">{t.contact.message}</label>
                <textarea data-testid="contact-message-input" rows={5} value={form.message} onChange={set("message")} className="field-input resize-y" />
              </div>
            )}

            <button
              type="submit" data-testid="contact-submit-button" disabled={sending}
              className="btn-outline-elegant w-full text-maquis-deep hover:bg-maquis-deep hover:text-casse disabled:opacity-50"
            >
              {sending ? t.contact.sending : t.contact.send}
            </button>
          </form>
          </div>
        </Reveal>
      </div>

      {/* Feed LOCATION */}
      <RelatedProperties testid="contact-feed" />
    </main>
  );
}
